import { Link } from "react-router-dom";
import { PublicPlans } from "@/components/billing/Plans";
import { Receipt, Smile, CalendarDays, MessagesSquare, Clapperboard, ArrowRight } from "lucide-react";
import { HouseQ, LegalFooter, Wordmark } from "@/components/brand/Brand";

const TILES = [
  { Icon: Receipt, label: "Money", copy: "Bills read by AI, spending by category.", rgb: "var(--glow-money)" },
  { Icon: Smile, label: "Kids Corner", copy: "Chores, a piggy bank and wish lists.", rgb: "var(--glow-kids)" },
  { Icon: CalendarDays, label: "Calendar", copy: "One calendar for the whole family.", rgb: "var(--glow-calendar)" },
  { Icon: MessagesSquare, label: "Connect", copy: "Chat and video, grandparents included.", rgb: "var(--glow-connect)" },
  { Icon: Clapperboard, label: "Media", copy: "Shortcuts to the apps you love.", rgb: "var(--glow-media)" },
];

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-q-bg" data-testid="landing-page">
      <div className="pointer-events-none absolute -left-40 top-24 h-[520px] w-[520px] rounded-full opacity-25 blur-[120px]" style={{ background: "rgb(var(--glow-home))" }} />
      <main className="relative mx-auto grid max-w-6xl gap-14 px-6 pb-16 pt-14 sm:px-10 lg:grid-cols-[1.15fr_1fr] lg:items-center lg:pt-24">
        <div className="order-2 lg:order-1 q-rise">
          <div className="q-eyebrow mb-5">A product of The Queue</div>
          <h1 className="font-display text-4xl font-extrabold leading-[1.02] tracking-[-0.03em] text-q-wordmark sm:text-5xl lg:text-6xl" data-testid="landing-title">
            The Queue Home Hub
          </h1>
          <p className="mt-3 text-xs font-bold uppercase tracking-[0.4em] text-q-muted" data-testid="landing-tagline">Software for you</p>
          <p className="mt-8 max-w-lg text-base leading-relaxed text-q-muted">
            One home hub for the whole family. Parents run bills and spending, kids earn and save, and everyone, grandparents
            included, stays connected.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/signup" className="q-btn q-btn-primary px-6 py-3" data-testid="landing-create-hub-btn">
              Create your hub <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/signin" className="q-btn q-btn-secondary px-6 py-3" data-testid="landing-signin-btn">Sign in</Link>
            <Link to="/kid" className="q-btn px-6 py-3 text-white" data-testid="landing-kid-btn"
              style={{ background: "rgba(var(--glow-kids),0.16)", boxShadow: "inset 0 0 0 1px rgba(var(--glow-kids),0.5), 0 0 22px rgba(var(--glow-kids),0.25)" }}>
              I'm a kid
            </Link>
          </div>
          <div className="mt-14 grid gap-3 sm:grid-cols-2">
            {TILES.map((t, i) => (
              <div key={t.label} className="flex items-start gap-3 rounded-xl border border-q-border bg-q-surface/70 p-4 q-rise" style={{ animationDelay: `${120 + i * 60}ms` }}>
                <t.Icon className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: `rgb(${t.rgb})`, filter: `drop-shadow(0 0 6px rgba(${t.rgb},0.7))` }} />
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: `rgb(${t.rgb})` }}>{t.label}</div>
                  <div className="mt-1 text-sm text-q-muted">{t.copy}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="order-1 flex justify-center lg:order-2 q-rise" data-testid="brand-lockup">
          <div className="flex flex-col items-center text-center">
            <HouseQ size={220} className="h-[150px] w-[150px] sm:h-[220px] sm:w-[220px] drop-shadow-[0_0_60px_rgba(244,121,32,0.35)]" />
            <Wordmark tm className="mt-8 text-5xl sm:text-6xl" />
            <span className="mt-3 text-xs font-semibold uppercase tracking-[0.42em] text-q-muted">Software for you</span>
          </div>
        </div>
      </main>
      <section className="relative mx-auto max-w-6xl px-6 pb-16 sm:px-10" data-testid="landing-plans">
        <h2 className="mb-5 font-display text-2xl font-extrabold text-q-text">Plans</h2>
        <PublicPlans />
      </section>
      <LegalFooter className="relative border-t border-q-border" />
    </div>
  );
}
